import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X } from 'lucide-react';
import { navItems } from '../../data/navigation';
import Button from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import MobileMenu from './MobileMenu';
import logoImg from '../../assets/logo.png';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Gérer le changement de style et la fermeture du menu au scroll
  useEffect(() => {
    const handleScroll = () => {
      // Ferme le menu si l'utilisateur commence à scroller
      if (isOpen) {
        setIsOpen(false);
      }
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]); // Ajout de 'isOpen' aux dépendances

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled || isOpen ? 'bg-neutral-offwhite/95 backdrop-blur-sm shadow-lg py-2' : 'bg-transparent py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo avec animation */}
            <Link
              to="/"
              className="flex items-center gap-2 xl:gap-3 text-neutral-ink font-playfair text-lg xl:text-xl font-bold z-50 group"
              onClick={() => isOpen && setIsOpen(false)}
            >
              <div className="relative">
                {/* Cercles décoratifs animés en arrière-plan */}
                <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-accent-rose/20 via-accent-sand/20 to-brand/10 blur-xl animate-pulse opacity-50"></div>
                <div className="absolute -inset-1 rounded-full bg-accent-rose/20 blur-md animate-heartbeat opacity-60"></div>
                
                {/* Effet de brillance tournant */}
                <div className="absolute inset-0 rounded-full overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent animate-shine"></div>
                </div>
                
                {/* Logo principal */}
                <img 
                  src={logoImg} 
                  alt="Logo Doux ce Moman" 
                  className="relative z-10 w-10 h-10 animate-heartbeat transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 drop-shadow-lg" 
                />
                
                {/* Halo au hover avec gradient */}
                <div className="absolute inset-0 rounded-full bg-gradient-radial from-brand/30 via-accent-rose/20 to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                {/* Particules flottantes */}
                <div className="absolute top-0 -right-1 w-1 h-1 rounded-full bg-accent-rose animate-float opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                <div className="absolute -top-1 right-1 w-1.5 h-1.5 rounded-full bg-brand/40 animate-float [animation-delay:0.5s] opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
              </div>
              <span className="relative">
                Doux ce Moman
                {/* Soulignement au hover */}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-accent-rose to-brand group-hover:w-full transition-all duration-500"></span>
              </span>
            </Link>

            {/* Desktop Navigation avec animations */}
            <div className="hidden lg:flex items-center lg:space-x-3 xl:space-x-6">
              {navItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-nunito text-sm xl:text-base whitespace-nowrap transition-all duration-300 relative group ${
                    location.pathname === item.path
                      ? 'text-brand font-semibold'
                      : 'text-neutral-body hover:text-brand'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="relative">
                    {item.label}
                    {/* Point décoratif animé */}
                    <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full transition-all duration-300 ${
                      location.pathname === item.path 
                        ? 'bg-brand scale-100' 
                        : 'bg-brand scale-0 group-hover:scale-100'
                    }`}></span>
                  </span>
                </Link>
              ))}
              <Button variant="primary" size="sm" to="/reservation">
                Prendre RDV
              </Button>
            </div>

            {/* Mobile menu button */}
            <motion.button
              className="lg:hidden text-neutral-ink focus:outline-none z-50"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Ouvrir le menu"
              animate={isOpen ? 'open' : 'closed'}
            >
              <motion.div
                variants={{
                  open: { rotate: 90, transition: { duration: 0.3 } },
                  closed: { rotate: 0, transition: { duration: 0.3 } },
                }}
              >
                {isOpen ? <X size={24} /> : <MenuIcon size={24} />}
              </motion.div>
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu using Framer Motion */}
      <AnimatePresence>
        {isOpen && <MobileMenu closeMenu={() => setIsOpen(false)} location={location} />}
      </AnimatePresence>
    </>
  );
};

export default Navbar;